import type { PageType } from 'lib/dot/enum/pageType.ts';
import type { BooleanAnalytics, LocalityAnalytics, PriceAnalytics } from 'lib/dot/types.ts';
import type { OfferData, OfferStatus, WhereSold } from 'service/offer/model.ts';
import { WithdrawalReason } from 'lib/dot/enum/withdrawalReason.ts';
import { DOTHelper } from 'lib/dot/DOTHelper.ts';
import { offerToLocalityAnalytics } from 'lib/dot/utils.ts';
import type { PaymentType } from 'lib/dot/enum/paymentType.ts';

/**
 * Every single `seller` analytics has these
 */
type SharedProperties = {
	action: 'seller';
	pageType: PageType;
};

/**
 * If a `seller` analytics event is related to an offer, it always has these properties
 */
type OfferProperties = {
	advertId: number;
	advertName: string;
	categoryId: number;
	hasDescription?: BooleanAnalytics;
	hasVideo: BooleanAnalytics;
	locality: LocalityAnalytics[];
	numPhotos: number;
	phonePublished: BooleanAnalytics;
	price: PriceAnalytics;
	sellerId: number;
};

/**
 * When a user clicks-through to create a new offer
 */
export type CreateOfferAnalytics = SharedProperties & {
	actionType: 'createAdvert';
	section: 'other' | 'ribbon' | 'sellerProfile';
};
export function sendCreateOfferAnalytics(section: CreateOfferAnalytics['section']) {
	// #if [DEV]
	DOTHelper.checkStore(['pageType']);
	// #endif
	const store = DOTHelper.getStore();

	const data: CreateOfferAnalytics = {
		action: 'seller',
		actionType: 'createAdvert',
		pageType: store.pageType,
		section,
	};

	return DOTHelper.hit(data);
}

/**
 * When a user navigates to edit an existing offer (i.e. not the actual edit action, that is handled by publish offer)
 */
export type EditOfferAnalytics = SharedProperties & {
	actionType: 'editAdvert';
	advertId: number;
};
export function sendEditOfferAnalytics(offerId: number) {
	// #if [DEV]
	DOTHelper.checkStore(['pageType']);
	// #endif
	const store = DOTHelper.getStore();

	const data: EditOfferAnalytics = {
		action: 'seller',
		actionType: 'editAdvert',
		pageType: store.pageType,
		advertId: offerId,
	};

	return DOTHelper.hit(data);
}

// Sent both when publishing an already created offer or creating a new offer
export type PublishOfferAnalytics = SharedProperties &
	OfferProperties & {
		actionType: 'publishAdvert';
		advertStatus: 'new' | 'edited' | 'inactive'; // This is to be different from OfferStatus for this specific action...
	};

/**
 *
 * @param offer
 * @param offerStatus not your regular OfferStatus, but a status of the offer in the context of publishing
 * @param sellerId
 */
export function sendPublishOfferAnalytics(
	offer: Pick<
		OfferData,
		| 'description'
		| 'dontShowPhone'
		| 'id'
		| 'images'
		| 'localities'
		| 'locality'
		| 'name'
		| 'price'
		| 'priceByAgreement'
		| 'videos'
	> & { category: { id: number } },
	offerStatus: 'new' | 'edited' | 'inactive',
	sellerId: number,
) {
	// #if [DEV]
	DOTHelper.checkStore(['pageType']);
	// #endif
	const store = DOTHelper.getStore();

	const data: PublishOfferAnalytics = {
		action: 'seller',
		actionType: 'publishAdvert',
		advertId: offer.id,
		advertName: offer.name,
		advertStatus: offerStatus,
		categoryId: offer.category.id,
		hasDescription: DOTHelper.sanitizeBoolean(offer.description && offer.description?.length > 0),
		hasVideo: DOTHelper.sanitizeBoolean(offer.videos && offer.videos.length > 0),
		locality: offerToLocalityAnalytics(offer),
		numPhotos: offer.images?.length ?? 0,
		pageType: store.pageType,
		phonePublished: DOTHelper.sanitizeBoolean(!offer.dontShowPhone),
		price: offer.priceByAgreement ? 'dohodou' : offer.price,
		sellerId,
	};

	return DOTHelper.hit(data);
}

export type TopOfferAnalytics = SharedProperties &
	OfferProperties & {
		actionType: 'topAdvert';
		advertStatus: OfferStatus;
		option: 1 | 3 | 5;
		paymentType: PaymentType;
	};

export function sendTopOfferAnalytics(offer: OfferData, option: 1 | 3 | 5, paymentType: PaymentType, sellerId: number) {
	// #if [DEV]
	DOTHelper.checkStore(['pageType']);
	// #endif
	const store = DOTHelper.getStore();

	const data: TopOfferAnalytics = {
		action: 'seller',
		actionType: 'topAdvert',
		advertId: offer.id,
		advertName: offer.name,
		advertStatus: offer.status,
		categoryId: offer.category.id,
		hasDescription: DOTHelper.sanitizeBoolean(offer.description && offer.description?.length > 0),
		hasVideo: DOTHelper.sanitizeBoolean(offer.videos && offer.videos.length > 0),
		locality: offerToLocalityAnalytics(offer),
		numPhotos: offer.images?.length ?? 0,
		option: option,
		pageType: store.pageType,
		paymentType: paymentType,
		phonePublished: DOTHelper.sanitizeBoolean(!offer.dontShowPhone),
		price: offer.priceByAgreement ? 'dohodou' : offer.price,
		sellerId,
	};

	return DOTHelper.hit(data);
}

const whereSoldToWithdrawalReason: Record<WhereSold, WithdrawalReason> = {
	not_specified: WithdrawalReason.UNSPECIFIED,
	sold_sbazar: WithdrawalReason.SOLD_SBAZAR,
	sold_other: WithdrawalReason.SOLD_OTHER,
} as const;

export type WithdrawOfferAnalytics = SharedProperties &
	OfferProperties & {
		actionType: 'withdrawAdvert';
		advertStatus: OfferStatus;
		withdrawalReason: WithdrawalReason;
	};

export function sendWithdrawOfferAnalytics(offer: OfferData, sellerId: number, withdrawalReason: WhereSold) {
	// #if [DEV]
	DOTHelper.checkStore(['pageType']);
	// #endif
	const store = DOTHelper.getStore();

	const data: WithdrawOfferAnalytics = {
		action: 'seller',
		actionType: 'withdrawAdvert',
		advertId: offer.id,
		advertName: offer.name,
		advertStatus: offer.status,
		categoryId: offer.category.id,
		hasDescription: DOTHelper.sanitizeBoolean(offer.description && offer.description?.length > 0),
		hasVideo: DOTHelper.sanitizeBoolean(offer.videos && offer.videos.length > 0),
		locality: offerToLocalityAnalytics(offer),
		numPhotos: offer.images?.length ?? 0,
		pageType: store.pageType,
		phonePublished: DOTHelper.sanitizeBoolean(!offer.dontShowPhone),
		price: offer.priceByAgreement ? 'dohodou' : offer.price,
		sellerId,
		withdrawalReason: whereSoldToWithdrawalReason[withdrawalReason],
	};

	return DOTHelper.hit(data);
}

export type DeleteOfferAnalytics = SharedProperties &
	OfferProperties & {
		actionType: 'deleteAdvert';
		advertStatus: OfferStatus;
		withdrawalReason: WithdrawalReason;
	};

export function sendDeleteOfferAnalytics(offer: OfferData, sellerId: number, withdrawalReason: WhereSold) {
	// #if [DEV]
	DOTHelper.checkStore(['pageType']);
	// #endif
	const store = DOTHelper.getStore();

	const data: DeleteOfferAnalytics = {
		action: 'seller',
		actionType: 'deleteAdvert',
		advertId: offer.id,
		advertName: offer.name,
		advertStatus: offer.status,
		categoryId: offer.category.id,
		hasDescription: DOTHelper.sanitizeBoolean(offer.description && offer.description?.length > 0),
		hasVideo: DOTHelper.sanitizeBoolean(offer.videos && offer.videos.length > 0),
		locality: offerToLocalityAnalytics(offer),
		numPhotos: offer.images?.length ?? 0,
		pageType: store.pageType,
		phonePublished: DOTHelper.sanitizeBoolean(!offer.dontShowPhone),
		price: offer.priceByAgreement ? 'dohodou' : offer.price,
		sellerId,
		withdrawalReason: whereSoldToWithdrawalReason[withdrawalReason],
	};

	return DOTHelper.hit(data);
}

export type ReserveOfferAnalytics = SharedProperties &
	OfferProperties & {
		actionType: 'advertReserved' | 'reservationCancelled';
		advertStatus: OfferStatus;
	};

export function sendReserveOfferAnalytics(offer: OfferData, sellerId: number, reserved: boolean) {
	// #if [DEV]
	DOTHelper.checkStore(['pageType']);
	// #endif
	const store = DOTHelper.getStore();

	const data: ReserveOfferAnalytics = {
		action: 'seller',
		actionType: reserved ? 'advertReserved' : 'reservationCancelled',
		advertId: offer.id,
		advertName: offer.name,
		advertStatus: offer.status,
		categoryId: offer.category.id,
		hasDescription: DOTHelper.sanitizeBoolean(offer.description && offer.description?.length > 0),
		hasVideo: DOTHelper.sanitizeBoolean(offer.videos && offer.videos.length > 0),
		locality: offerToLocalityAnalytics(offer),
		numPhotos: offer.images?.length ?? 0,
		pageType: store.pageType,
		phonePublished: DOTHelper.sanitizeBoolean(!offer.dontShowPhone),
		price: offer.priceByAgreement ? 'dohodou' : offer.price,
		sellerId,
	};

	return DOTHelper.hit(data);
}

/**
 * When a user clicks-through to view their own offers
 */
export type MyOffersAnalytics = SharedProperties & {
	actionType: 'myAdverts';
	sellerId: number;
};
export function sendMyOffersAnalytics(sellerId: number) {
	// #if [DEV]
	DOTHelper.checkStore(['pageType']);
	// #endif
	const store = DOTHelper.getStore();

	const data: MyOffersAnalytics = {
		action: 'seller',
		actionType: 'myAdverts',
		pageType: store.pageType,
		sellerId,
	};

	return DOTHelper.hit(data);
}

/**
 * When a user clicks-through to edit their profile
 */
export type EditProfileAnalytics = SharedProperties & {
	actionType: 'myProfile';
	sellerId: number;
};
export function sendEditProfileAnalytics(sellerId: number) {
	// #if [DEV]
	DOTHelper.checkStore(['pageType']);
	// #endif
	const store = DOTHelper.getStore();

	const data: EditProfileAnalytics = {
		action: 'seller',
		actionType: 'myProfile',
		pageType: store.pageType,
		sellerId,
	};

	return DOTHelper.hit(data);
}

/**
 * When a user clicks-through to verify their profile
 */
export type VerifyProfileOpenModalAnalytics = SharedProperties & {
	actionType: 'authenticateProfile';
	sellerId: number;
};
export function sendVerifyProfileOpenModalAnalytics(sellerId: number) {
	// #if [DEV]
	DOTHelper.checkStore(['pageType']);
	// #endif
	const store = DOTHelper.getStore();

	const data: VerifyProfileOpenModalAnalytics = {
		action: 'seller',
		actionType: 'authenticateProfile',
		pageType: store.pageType,
		sellerId,
	};

	return DOTHelper.hit(data);
}

export type SaveProfileAnalytics = SharedProperties & {
	actionType: 'saveProfile';
	hasDescription: BooleanAnalytics;
	hasPhoto: BooleanAnalytics;
	sellerId: number;
};

export function sendSaveProfileAnalytics(sellerId: number, hasPhoto: boolean, hasDescription: boolean) {
	// #if [DEV]
	DOTHelper.checkStore(['pageType']);
	// #endif
	const store = DOTHelper.getStore();

	const data: SaveProfileAnalytics = {
		action: 'seller',
		actionType: 'saveProfile',
		hasDescription: DOTHelper.sanitizeBoolean(hasDescription),
		hasPhoto: DOTHelper.sanitizeBoolean(hasPhoto),
		pageType: store.pageType,
		sellerId,
	};

	return DOTHelper.hit(data);
}

export type SendSavePhotoAnalytics = SharedProperties & {
	actionType: 'saveProfilePhoto';
	sellerId: number;
};

export type SendRemovePhotoAnalytics = SharedProperties & {
	actionType: 'removeProfilePhoto';
	sellerId: number;
};

export function sendSavePhotoAnalytics(sellerId: number) {
	// #if [DEV]
	DOTHelper.checkStore(['pageType']);
	// #endif
	const store = DOTHelper.getStore();

	const data: SendSavePhotoAnalytics = {
		action: 'seller',
		actionType: 'saveProfilePhoto',
		pageType: store.pageType,
		sellerId,
	};

	return DOTHelper.hit(data);
}

export function sendRemovePhotoAnalytics(sellerId: number) {
	// #if [DEV]
	DOTHelper.checkStore(['pageType']);
	// #endif
	const store = DOTHelper.getStore();

	const data: SendRemovePhotoAnalytics = {
		action: 'seller',
		actionType: 'removeProfilePhoto',
		pageType: store.pageType,
		sellerId,
	};

	return DOTHelper.hit(data);
}
